import type { ClientRoomState, SpotifyAuthStatus } from '@hitster/shared';
import { MIN_PLAYABLE_TRACKS } from '@hitster/shared';
import { useT } from '../i18n.tsx';
import { Alert, LanguageSwitch } from '../components/common.tsx';
import { PlayerList } from '../components/PlayerList.tsx';
import { PlaylistPicker } from '../components/PlaylistPicker.tsx';
import { QrJoin } from '../components/QrJoin.tsx';
import { SettingsPanel } from '../components/SettingsPanel.tsx';
import { AddPlayerRow } from '../components/AddPlayerRow.tsx';
import { RoomMenu } from '../components/RoomMenu.tsx';
import { ScopeWarning } from '../components/ScopeWarning.tsx';
import { SpeakerControls, SpeakerMenu } from '../components/SpeakerMenu.tsx';
import type { RoomApi } from '../net/useRoom.ts';

/**
 * Lobby: beitreten lassen, Playlist waehlen, Regeln setzen, starten.
 *
 * Host und Mitspieler sehen dieselbe Seite; die Host-Werkzeuge werden
 * nur fuer den Host gerendert.
 */
export function LobbyPage({
  state,
  api,
  authStatus,
}: {
  state: ClientRoomState;
  api: RoomApi;
  authStatus: SpotifyAuthStatus | null;
}) {
  const t = useT();
  const isHost = state.hostId === state.youId;

  const playlistReady =
    state.playlist !== null && state.playlist.usableTrackCount >= MIN_PLAYABLE_TRACKS;

  return (
    <div className="page">
      <div className="row row--between">
        <h2>{t('lobby.title')}</h2>
        <div className="row">
          <LanguageSwitch />
          {/* Ton an derselben Stelle wie im Spiel -- man sucht ihn nicht
              zweimal woanders. */}
          <SpeakerMenu state={state} api={api} authStatus={authStatus} />
          {/* Verlassen mit Rueckfrage -- ein versehentliches ✕ hat
              vorher den Raum ohne Warnung geschlossen. */}
          <RoomMenu state={state} api={api} />
        </div>
      </div>

      {/* Fehlende Berechtigungen genau dort melden, wo der Host die
          Playlist laedt -- sonst sieht er nur einen 403 ohne Ursache. */}
      <ScopeWarning status={authStatus} />

      <div className="grid-2">
        {/* ---------- Linke Spalte: Host-Werkzeuge ---------- */}
        <div className="stack">
          {isHost ? (
            <>
              <PlaylistPicker state={state} api={api} />
              <SettingsPanel
                settings={state.settings}
                disabled={false}
                onChange={api.updateSettings}
              />

            </>
          ) : (
            <div className="panel">
              <div className="panel__title">{t('playlist.title')}</div>
              {state.playlist ? (
                <div className="row">
                  {state.playlist.imageUrl && (
                    <img className="playlist-item__cover" src={state.playlist.imageUrl} alt="" />
                  )}
                  <div className="min-w-0">
                    <div className="playlist-item__name">{state.playlist.name}</div>
                    <div className="playlist-item__meta">
                      {t('playlist.tracks', { count: state.playlist.usableTrackCount })}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-dim">{t('lobby.waitingForHost')}</p>
              )}
            </div>
          )}

        </div>

        {/* ---------- Rechte Spalte: Beitritt und Spieler ---------- */}
        <div className="stack">
          <div className="panel">
            <QrJoin joinUrl={state.joinUrl} code={state.code} />
          </div>

          {/**
           * Ton nur dann prominent, wenn er fehlt.
           *
           * Ein Dauerblock „Lautsprecher" nimmt Platz weg, sobald das
           * Thema erledigt ist. Ohne Ton ist die Runde aber unspielbar --
           * also laut, solange es fehlt, und still, sobald es läuft.
           */}
          {state.audioDeviceCount === 0 && (
            <div className="callout">
              <div className="stack stack--tight">
                <strong>{t('audio.none')}</strong>
                <span className="text-sm text-muted">{t('audio.explain')}</span>
                <SpeakerControls state={state} api={api} authStatus={authStatus} />
              </div>
            </div>
          )}

          <div className="panel">
            <div className="row row--between" style={{ marginBottom: '0.75rem' }}>
              <span className="panel__title" style={{ margin: 0 }}>
                {t('lobby.players')} ({state.players.length})
              </span>

              {/* Direkt bei der Spielerliste, weil es genau darum geht:
                  belege ich einen Platz oder nicht. Gilt auch für den
                  Host — wer nur die Musik stellt, muss nicht mitspielen. */}
              <label className="switch" style={{ minHeight: 0, padding: 0 }}>
                <input
                  type="checkbox"
                  checked={!state.youAreSpectator}
                  onChange={(event) => api.setPlaying(event.target.checked)}
                />
                <span className="text-sm">{t('role.playAlong')}</span>
              </label>
            </div>

            <PlayerList
              state={state}
              showKick={isHost}
              onKick={api.kick}
              onRemoveLocal={api.removeLocalPlayer}
            />

            {/* Mitspieler ohne eigenes Gerät direkt hier anlegen -- sie
                erscheinen unmittelbar darüber in derselben Liste. */}
            {isHost && state.phase === 'lobby' && <AddPlayerRow api={api} />}

            <span className="field__hint" style={{ display: 'block', marginTop: '0.75rem' }}>
              {state.youAreSpectator ? t('role.spectatingHint') : t('role.playingHint')}
            </span>
          </div>

          {/* Zuschauer belegen keinen Spielplatz, sollen aber sichtbar
              sein -- sie koennen den Ton ausgeben. */}
          {state.spectators.length > 0 && (
            <div className="panel">
              <div className="panel__title">
                {t('spectator.title')} ({state.spectators.length})
              </div>
              <div className="stack stack--tight">
                {state.spectators.map((spectator) => (
                  <div key={spectator.id} className="player-row">
                    <span className="player-row__name" style={{ flex: 1 }}>
                      {spectator.name}
                    </span>
                    {spectator.id === state.youId && (
                      <span className="badge badge--you">{t('common.you')}</span>
                    )}
                    {spectator.isAudioDevice && <span className="badge badge--speaker">?</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {isHost ? (
            <div className="stack stack--tight">
              {!state.playlist && <Alert kind="info">{t('lobby.needPlaylist')}</Alert>}
              {state.playlist && !playlistReady && (
                <Alert kind="error">{t('lobby.needTracks', { min: MIN_PLAYABLE_TRACKS })}</Alert>
              )}
              <button
                type="button"
                className="btn btn--primary btn--lg btn--block"
                disabled={!playlistReady}
                onClick={api.startGame}
              >
                {t('lobby.start')}
              </button>
            </div>
          ) : (
            <Alert kind="info">{t('lobby.waitingForHost')}</Alert>
          )}
        </div>
      </div>
    </div>
  );
}
